import { getAccessToken } from "../utils/auth";
import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../utils/api";
import {
  Typography,
  Button,
  Snackbar,
  Alert,
  IconButton,
  Box,
  Avatar,
  Chip,
  Tooltip,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  InputAdornment,
  Paper,
  Grid,
  Stack,
  MenuItem,
  CircularProgress,
  Container,
  InputBase,
} from "@mui/material";
import {
  ArrowBack,
  Edit,
  Delete,
  ToggleOn,
  ToggleOff,
  Person,
  Lock,
  Search,
} from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import {
  ADMIN_ROLES,
  getRoleLabel,
  isAdminProfileIncomplete,
  isValidDateString,
} from "../constants/adminRoles";
import {
  QUALIFICATION_GROUPS,
  resolveQualification,
} from "../constants/adminQualifications";

const AdminDetails = () => {
  const [admins, setAdmins] = useState([]);
  const [filteredAdmins, setFilteredAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [newEmployeeId, setNewEmployeeId] = useState("");
  const [newPhoneNumber, setNewPhoneNumber] = useState("");
  const [newDob, setNewDob] = useState("");
  const [newDateOfJoin, setNewDateOfJoin] = useState("");
  const [qualificationOption, setQualificationOption] = useState("");
  const [qualificationManual, setQualificationManual] = useState("");
  const [isManualQualification, setIsManualQualification] = useState(false);
  const [newRole, setNewRole] = useState("");
  const [newManualMapping, setNewManualMapping] = useState("enabled");
  const [newObjectDisinfection, setNewObjectDisinfection] = useState("enabled");
  const [formErrors, setFormErrors] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [newStatus, setNewStatus] = useState("active");
  const [searchTerm, setSearchTerm] = useState("");
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();

  const newQualification = useMemo(
    () =>
      resolveQualification(
        qualificationOption,
        qualificationManual,
        isManualQualification
      ),
    [qualificationOption, qualificationManual, isManualQualification]
  );

  useEffect(() => {
    const fetchAdmins = async () => {
      try {
        const token = getAccessToken();
        const response = await axios.get("/get-admin-details", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setAdmins(response.data);
        setFilteredAdmins(response.data);
        setLoading(false);
      } catch (error) {
        setError("Error fetching admin details");
        setLoading(false);
      }
    };

    fetchAdmins();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = admins.filter(
        (admin) =>
          admin.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          admin.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (admin.employeeId &&
            admin.employeeId
              .toLowerCase()
              .includes(searchTerm.toLowerCase())) ||
          (admin.phoneNumber && admin.phoneNumber.includes(searchTerm)) ||
          (admin.role &&
            admin.role.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (admin.qualification &&
            admin.qualification
              .toLowerCase()
              .includes(searchTerm.toLowerCase()))
      );
      setFilteredAdmins(filtered);
    } else {
      setFilteredAdmins(admins);
    }
  }, [searchTerm, admins]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  const confirmDeleteAdmin = async () => {
    if (!adminToDelete) return;
    try {
      const token = getAccessToken();
      await axios.delete("/admin-details-delete", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: { email: adminToDelete },
      });

      const updatedAdmins = admins.filter(
        (admin) => admin.email !== adminToDelete
      );
      setAdmins(updatedAdmins);

      setSnackbarMessage("Admin user deleted successfully");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error deleting admin:", error);
      setSnackbarMessage("Error deleting admin");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setConfirmDialogOpen(false);
      setAdminToDelete(null);
    }
  };

  const validateFields = () => {
    const errors = {};

    if (!newEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      errors.email = "Invalid email format";
    }

    if (!newName.match(/^[a-zA-Z\s]+$/)) {
      errors.name = "Name can only contain letters and spaces";
    }

    if (!newPhoneNumber.match(/^\d{10}$/)) {
      errors.phoneNumber = "Invalid phone number format (10 digits)";
    }

    if (newDob && !isValidDateString(newDob)) {
      errors.dob = "Date of birth must be in YYYY-MM-DD format";
    }

    if (newDateOfJoin && !isValidDateString(newDateOfJoin)) {
      errors.dateOfJoin = "Date of join must be in YYYY-MM-DD format";
    }

    if (newQualification.trim() && newQualification.trim().length > 200) {
      errors.qualification = "Qualification must be 200 characters or fewer";
    }

    return errors;
  };

  const formatDateField = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toISOString().split("T")[0];
  };

  const buildUpdatePayload = (admin, overrides = {}) => {
    const payload = {
      oldEmail: admin.email,
      newEmail: overrides.newEmail ?? admin.email,
      newName: overrides.newName ?? admin.name,
      newEmployeeId: overrides.newEmployeeId ?? admin.employeeId,
      newPhoneNumber: overrides.newPhoneNumber ?? admin.phoneNumber,
      newDob: overrides.newDob ?? formatDateField(admin.dob),
      newRole: overrides.newRole ?? admin.role,
      newStatus: overrides.newStatus ?? admin.status,
      manualMapping: overrides.manualMapping ?? admin.manualMapping ?? "enabled",
      objectDisinfection:
        overrides.objectDisinfection ?? admin.objectDisinfection ?? "enabled",
    };

    const dateOfJoin =
      overrides.newDateOfJoin !== undefined
        ? overrides.newDateOfJoin
        : formatDateField(admin.dateOfJoin);
    if (dateOfJoin) {
      payload.newDateOfJoin = dateOfJoin;
    }

    const qualification =
      overrides.newQualification !== undefined
        ? overrides.newQualification.trim()
        : (admin.qualification || "").trim();
    if (qualification) {
      payload.newQualification = qualification;
    }

    return payload;
  };

  const handleUpdateDetails = async () => {
    const validationErrors = validateFields();
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      return;
    }

    if (
      !selectedAdmin ||
      !newEmail ||
      !newName ||
      !newEmployeeId ||
      !newRole ||
      !newPhoneNumber ||
      !newDob ||
      !newStatus
    )
      return;

    setIsSubmitting(true);
    try {
      const token = getAccessToken();
      const response = await axios.put(
        `/admin-details-update`,
        buildUpdatePayload(selectedAdmin, {
          newEmail,
          newName,
          newEmployeeId,
          newPhoneNumber,
          newDob,
          newDateOfJoin,
          newQualification,
          newRole,
          newStatus,
          manualMapping: newManualMapping,
          objectDisinfection: newObjectDisinfection,
        }),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const updatedAdmins = admins.map((admin) =>
        admin.email === selectedAdmin.email
          ? {
              ...admin,
              email: newEmail,
              name: newName,
              employeeId: newEmployeeId,
              phoneNumber: newPhoneNumber,
              dob: newDob,
              dateOfJoin: newDateOfJoin || admin.dateOfJoin,
              qualification: newQualification.trim() || admin.qualification,
              role: newRole,
              status: newStatus,
              manualMapping: newManualMapping,
              objectDisinfection: newObjectDisinfection,
            }
          : admin
      );
      setAdmins(updatedAdmins);
      setOpenDialog(false);
      setSelectedAdmin(null);
      setNewEmail("");
      setNewName("");
      setNewEmployeeId("");
      setNewPhoneNumber("");
      setNewDob("");
      setNewDateOfJoin("");
      setQualificationOption("");
      setQualificationManual("");
      setIsManualQualification(false);
      setNewRole("");
      setNewManualMapping("enabled");
      setNewObjectDisinfection("enabled");
      setNewStatus("active");
      setFormErrors({});

      let snackMessage =
        response.data?.message || "Admin details updated successfully";
      if (response.data?.updateEmailWarning) {
        snackMessage = `${snackMessage}. Update email could not be sent.`;
        setSnackbarSeverity("warning");
      } else {
        setSnackbarSeverity("success");
      }
      setSnackbarMessage(snackMessage);
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Error updating admin details:", err);
      setSnackbarMessage(
        err.response?.data?.message || "Failed to update admin details"
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleStatus = async (admin) => {
    const token = getAccessToken();
    const newStatus = admin.status === "active" ? "inactive" : "active";

    try {
      await axios.put(
        `/admin-details-update`,
        buildUpdatePayload(admin, { newStatus }),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const updatedAdmins = admins.map((a) =>
        a.email === admin.email ? { ...a, status: newStatus } : a
      );
      setAdmins(updatedAdmins);

      setSnackbarSeverity("success");
      setSnackbarMessage(`Status updated to ${newStatus}`);
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Error toggling status:", err);
      setSnackbarSeverity("error");
      setSnackbarMessage("Failed to update status");
      setSnackbarOpen(true);
    }
  };

  const openUpdateDialog = (admin) => {
    setSelectedAdmin(admin);
    setNewEmail(admin.email);
    setNewName(admin.name);
    setNewEmployeeId(admin.employeeId);
    setNewPhoneNumber(admin.phoneNumber);
    setNewDob(formatDateField(admin.dob));
    setNewDateOfJoin(formatDateField(admin.dateOfJoin));
    setQualificationOption("");
    setQualificationManual("");
    setIsManualQualification(false);
    setNewRole(admin.role);
    setNewManualMapping(admin.manualMapping || "enabled");
    setNewObjectDisinfection(admin.objectDisinfection || "enabled");
    setOpenDialog(true);
    setNewStatus(admin.status);
  };

  const closeUpdateDialog = () => {
    setOpenDialog(false);
    setSelectedAdmin(null);
    setNewEmail("");
    setNewName("");
    setNewEmployeeId("");
    setNewPhoneNumber("");
    setNewDob("");
    setNewDateOfJoin("");
    setQualificationOption("");
    setQualificationManual("");
    setIsManualQualification(false);
    setNewRole("");
    setNewManualMapping("enabled");
    setNewObjectDisinfection("enabled");
    setFormErrors({});
    setNewStatus("active");
  };

  const handleQualificationOptionChange = (e) => {
    const value = e.target.value;
    setIsManualQualification(false);
    setQualificationOption(value);
    setQualificationManual("");
  };

  const handleManualQualificationSelect = () => {
    setIsManualQualification(true);
    setQualificationOption("");
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const getInitials = (name) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          background:
            "radial-gradient(1200px 600px at 10% 0%, rgba(99, 102, 241, 0.08), transparent 50%), radial-gradient(900px 450px at 90% 20%, rgba(16, 185, 129, 0.06), transparent 45%), #fafbfc",
        }}
      >
        <CircularProgress
          size={52}
          thickness={3.6}
          sx={{ color: "#6366f1" }}
        />
        <Typography variant="body1" sx={{ fontWeight: 600, color: "#64748b" }}>
          Loading admins...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
          background:
            "radial-gradient(1200px 600px at 10% 0%, rgba(99, 102, 241, 0.06), transparent 50%), #fafbfc",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            textAlign: "center",
            maxWidth: 440,
            width: "100%",
            bgcolor: "#fff",
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 2,
              bgcolor: "#fef2f2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <Lock sx={{ fontSize: 32, color: "#ef4444" }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", mb: 1 }}>
            Access Error
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {error}. Check your permissions or try again.
          </Typography>
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              borderRadius: 2,
              bgcolor: "#6366f1",
              "&:hover": { bgcolor: "#4f46e5" },
            }}
          >
            Retry
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "radial-gradient(1400px 700px at 0% 0%, rgba(99, 102, 241, 0.06), transparent 45%), radial-gradient(1000px 500px at 100% 0%, rgba(16, 185, 129, 0.05), transparent 40%), #f8fafc",
      }}
    >
      <Container maxWidth="xl" sx={{ py: { xs: 2.5, md: 4 }, px: { xs: 2, md: 3 } }}>
        <Stack spacing={3}>
          {/* Header */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
            spacing={2}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <IconButton
                onClick={() => navigate(-1)}
                sx={{
                  bgcolor: "#fff",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  "&:hover": { bgcolor: "#f8fafc", borderColor: "#cbd5e1" },
                }}
              >
                <ArrowBack sx={{ color: "#475569" }} />
              </IconButton>
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    color: "#0f172a",
                    letterSpacing: "-0.02em",
                    lineHeight: 1.2,
                  }}
                >
                  Admin Management
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  Manage and update administrator accounts
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                label={`Active: ${admins.filter((a) => a.status === "active").length}`}
                sx={{
                  height: 32,
                  fontWeight: 600,
                  fontSize: "0.8125rem",
                  bgcolor: "rgba(16, 185, 129, 0.12)",
                  color: "#059669",
                  border: "1px solid rgba(16, 185, 129, 0.3)",
                }}
              />
              <Chip
                size="small"
                label={`Inactive: ${admins.filter((a) => a.status === "inactive").length}`}
                sx={{
                  height: 32,
                  fontWeight: 600,
                  fontSize: "0.8125rem",
                  bgcolor: "rgba(239, 68, 68, 0.1)",
                  color: "#dc2626",
                  border: "1px solid rgba(239, 68, 68, 0.25)",
                }}
              />
              <Chip
                icon={<Person sx={{ fontSize: 18, color: "inherit" }} />}
                label={`${admins.length} Admin${admins.length !== 1 ? "s" : ""}`}
                sx={{
                  height: 40,
                  px: 1.5,
                  fontWeight: 700,
                  fontSize: "0.875rem",
                  bgcolor: "rgba(13, 148, 136, 0.1)",
                  color: "#0d9488",
                  border: "1px solid rgba(13, 148, 136, 0.2)",
                  "& .MuiChip-icon": { ml: 0.5 },
                }}
              />
            </Stack>
          </Stack>

          {/* Search + count */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
            spacing={2}
          >
            <Paper
              elevation={0}
              component="label"
              sx={{
                display: "flex",
                alignItems: "center",
                width: { xs: "100%", sm: 320 },
                height: 42,
                px: 2,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "#fff",
                "&:hover": { borderColor: "#94a3b8" },
                "&:focus-within": {
                  borderColor: "#6366f1",
                  boxShadow: "0 0 0 2px rgba(99, 102, 241, 0.15)",
                },
              }}
            >
              <Search sx={{ color: "#94a3b8", mr: 1.5, fontSize: 22 }} />
              <InputBase
                placeholder="Search admins..."
                value={searchTerm}
                onChange={handleSearchChange}
                sx={{ flex: 1, fontSize: "0.9375rem" }}
                endAdornment={
                  searchTerm ? (
                    <IconButton size="small" onClick={clearSearch} sx={{ p: 0.5 }}>
                      <CloseIcon sx={{ fontSize: 18, color: "#64748b" }} />
                    </IconButton>
                  ) : null
                }
              />
            </Paper>
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              Showing {filteredAdmins.length} of {admins.length} admins
            </Typography>
          </Stack>

          {/* Admin cards grid */}
          {filteredAdmins.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                p: 8,
                textAlign: "center",
                bgcolor: "#fff",
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: 2,
                  bgcolor: "#f1f5f9",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                }}
              >
                <Search sx={{ fontSize: 40, color: "#94a3b8" }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#334155" }}>
                No admins found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 400, mx: "auto" }}>
                No matching admins for "{searchTerm}". Try a different search.
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {filteredAdmins.map((admin) => (
                <Grid item xs={12} sm={6} md={4} lg={4} key={admin.email}>
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      position: "relative",
                      overflow: "hidden",
                      borderRadius: 3,
                      bgcolor: "#fff",
                      border: "1px solid",
                      borderColor: "rgba(99, 102, 241, 0.12)",
                      transition: "all 0.25s ease",
                      "&:hover": {
                        transform: "translateY(-6px)",
                        boxShadow: "0 20px 40px -12px rgba(99, 102, 241, 0.25)",
                        borderColor: "rgba(99, 102, 241, 0.25)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "4px",
                        background: "linear-gradient(90deg, #6366f1, #8b5cf6)",
                      }}
                    />
                    <CardContent sx={{ flexGrow: 1, p: 2.5, display: "flex", flexDirection: "column", gap: 1.5, "&:last-child": { pb: 2.5 } }}>
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
                        <Avatar
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2,
                            background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: "1rem",
                            boxShadow: "0 4px 12px rgba(99, 102, 241, 0.35)",
                          }}
                        >
                          {getInitials(admin.name)}
                        </Avatar>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography sx={{ fontWeight: 600, color: "#0f172a", fontSize: "0.9375rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {admin.name || "Unnamed Admin"}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#64748b", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {admin.email}
                          </Typography>
                          {isAdminProfileIncomplete(admin) && (
                            <Chip
                              size="small"
                              label="Profile incomplete"
                              sx={{
                                mt: 0.75,
                                height: 20,
                                fontSize: "0.65rem",
                                fontWeight: 600,
                                bgcolor: "rgba(245, 158, 11, 0.12)",
                                color: "#d97706",
                              }}
                            />
                          )}
                        </Box>
                      </Stack>

                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.25,
                          py: 1.5,
                          borderTop: "1px solid #f1f5f9",
                          borderBottom: "1px solid #f1f5f9",
                        }}
                      >
                        {admin.employeeId != null && admin.employeeId !== "" && (
                          <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography variant="caption" color="text.secondary" fontWeight={500}>
                              Employee ID
                            </Typography>
                            <Typography variant="caption" fontWeight={600} sx={{ color: "#334155" }}>
                              {admin.employeeId}
                            </Typography>
                          </Stack>
                        )}
                        {admin.role != null && admin.role !== "" && (
                          <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography variant="caption" color="text.secondary" fontWeight={500}>
                              Role
                            </Typography>
                            <Typography variant="caption" fontWeight={600} sx={{ color: "#334155" }}>
                              {getRoleLabel(admin.role)}
                            </Typography>
                          </Stack>
                        )}
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Typography variant="caption" color="text.secondary" fontWeight={500}>
                            Date of join
                          </Typography>
                          <Typography variant="caption" fontWeight={600} sx={{ color: "#334155" }}>
                            {admin.dateOfJoin ? formatDate(admin.dateOfJoin) : "—"}
                          </Typography>
                        </Stack>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Typography variant="caption" color="text.secondary" fontWeight={500}>
                            Qualification
                          </Typography>
                          <Typography
                            variant="caption"
                            fontWeight={600}
                            sx={{
                              color: "#334155",
                              maxWidth: "55%",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              textAlign: "right",
                            }}
                          >
                            {admin.qualification || "—"}
                          </Typography>
                        </Stack>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Typography variant="caption" color="text.secondary" fontWeight={500}>
                            Status
                          </Typography>
                          <Chip
                            size="small"
                            label={admin.status ?? "—"}
                            sx={{
                              height: 22,
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              textTransform: "capitalize",
                              bgcolor: admin.status === "active" ? "rgba(16, 185, 129, 0.12)" : "rgba(239, 68, 68, 0.1)",
                              color: admin.status === "active" ? "#059669" : "#dc2626",
                            }}
                          />
                        </Stack>
                        {admin.phoneNumber != null && admin.phoneNumber !== "" && (
                          <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography variant="caption" color="text.secondary" fontWeight={500}>
                              Phone
                            </Typography>
                            <Typography variant="caption" fontWeight={600} sx={{ color: "#334155" }}>
                              {admin.phoneNumber}
                            </Typography>
                          </Stack>
                        )}
                        {admin.dob != null && admin.dob !== "" && (
                          <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography variant="caption" color="text.secondary" fontWeight={500}>
                              DOB
                            </Typography>
                            <Typography variant="caption" fontWeight={600} sx={{ color: "#334155" }}>
                              {formatDate(admin.dob)}
                            </Typography>
                          </Stack>
                        )}
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Typography variant="caption" color="text.secondary" fontWeight={500}>
                            Manual mapping
                          </Typography>
                          <Chip
                            size="small"
                            label={admin.manualMapping || "enabled"}
                            sx={{
                              height: 22,
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              textTransform: "capitalize",
                              bgcolor:
                                admin.manualMapping === "disabled"
                                  ? "rgba(239, 68, 68, 0.1)"
                                  : "rgba(16, 185, 129, 0.12)",
                              color:
                                admin.manualMapping === "disabled" ? "#dc2626" : "#059669",
                            }}
                          />
                        </Stack>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Typography variant="caption" color="text.secondary" fontWeight={500}>
                            Object disinfection
                          </Typography>
                          <Chip
                            size="small"
                            label={admin.objectDisinfection || "enabled"}
                            sx={{
                              height: 22,
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              textTransform: "capitalize",
                              bgcolor:
                                admin.objectDisinfection === "disabled"
                                  ? "rgba(239, 68, 68, 0.1)"
                                  : "rgba(16, 185, 129, 0.12)",
                              color:
                                admin.objectDisinfection === "disabled" ? "#dc2626" : "#059669",
                            }}
                          />
                        </Stack>
                      </Box>

                      <Stack direction="row" spacing={1} sx={{ mt: "auto", pt: 1 }}>
                        <Tooltip title={admin.status === "inactive" ? "Activate" : "Deactivate"}>
                          <IconButton
                            onClick={(e) => { e.stopPropagation(); handleToggleStatus(admin); }}
                            size="small"
                            sx={{
                              flex: 1,
                              color: admin.status === "active" ? "#10b981" : "#ef4444",
                              border: "1px solid",
                              borderColor: admin.status === "active" ? "#10b981" : "#ef4444",
                              borderRadius: 2,
                              "&:hover": { bgcolor: admin.status === "active" ? "rgba(16, 185, 129, 0.08)" : "rgba(239, 68, 68, 0.08)" },
                            }}
                          >
                            {admin.status === "active" ? <ToggleOn fontSize="small" /> : <ToggleOff fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={(e) => { e.stopPropagation(); openUpdateDialog(admin); }}
                          disabled={admin.status === "inactive"}
                          startIcon={<Edit fontSize="small" />}
                          sx={{
                            flex: 1,
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            borderColor: "#6366f1",
                            color: "#6366f1",
                            borderRadius: 2,
                            "&:hover": { borderColor: "#4f46e5", bgcolor: "rgba(99, 102, 241, 0.06)" },
                            "&:disabled": { borderColor: "#cbd5e1", color: "#94a3b8" },
                          }}
                        >
                          Update
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setAdminToDelete(admin.email);
                            setConfirmDialogOpen(true);
                          }}
                          disabled={admin.status === "inactive"}
                          startIcon={<Delete fontSize="small" />}
                          sx={{
                            flex: 1,
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            borderColor: "#ef4444",
                            color: "#ef4444",
                            borderRadius: 2,
                            "&:hover": { borderColor: "#dc2626", bgcolor: "rgba(239, 68, 68, 0.06)" },
                            "&:disabled": { borderColor: "#cbd5e1", color: "#94a3b8" },
                          }}
                        >
                          Delete
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Stack>

        {/* Confirm delete dialog - single instance */}
        <Dialog
          open={confirmDialogOpen}
          onClose={() => setConfirmDialogOpen(false)}
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: "0 24px 80px rgba(0,0,0,0.12)",
              maxWidth: 400,
            },
          }}
        >
          <DialogTitle sx={{ fontWeight: 700, color: "#0f172a", pb: 1 }}>
            Confirm deletion
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ color: "#64748b" }}>
              Are you sure you want to delete this admin user? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, pt: 0 }}>
            <Button
              onClick={() => setConfirmDialogOpen(false)}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDeleteAdmin}
              color="error"
              variant="contained"
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>

      {/* Update Admin Details Dialog - Redesigned */}
      <Dialog
        open={openDialog}
        onClose={closeUpdateDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            boxShadow:
              "0 24px 80px rgba(99, 102, 241, 0.15), 0 8px 24px rgba(0,0,0,0.08)",
            maxHeight: "92vh",
          },
        }}
      >
        {/* Header with gradient */}
        <Box
          sx={{
            background:
              "linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)",
            color: "#fff",
            px: 3,
            py: 2.5,
            position: "relative",
          }}
        >
          <IconButton
            aria-label="Close"
            onClick={closeUpdateDialog}
            sx={{
              position: "absolute",
              right: 12,
              top: "50%",
              transform: "translateY(-50%)",
              color: "rgba(255,255,255,0.9)",
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.15)",
                color: "#fff",
              },
            }}
            size="medium"
          >
            <CloseIcon />
          </IconButton>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EditOutlinedIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <DialogTitle
                sx={{
                  color: "inherit",
                  fontWeight: 700,
                  fontSize: "1.5rem",
                  p: 0,
                  m: 0,
                }}
              >
                Update Admin Details
              </DialogTitle>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.25 }}>
                Edit account and profile information
              </Typography>
            </Box>
          </Stack>
        </Box>

        <DialogContent
          sx={{
            p: 0,
            bgcolor: "#f8fafc",
            overflowY: "auto",
          }}
        >
          <Box sx={{ p: 3 }}>
            {/* Account / Email card */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2,
                mb: 2.5,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "#fff",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  color: "#334155",
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <EmailOutlinedIcon sx={{ fontSize: 22, color: "#6366f1" }} />
                Account
              </Typography>
              <TextField
                fullWidth
                size="small"
                label="Email"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                error={!!formErrors.email}
                helperText={formErrors.email}
                placeholder="admin@example.com"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlinedIcon
                        sx={{ color: "#94a3b8", fontSize: 20 }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    backgroundColor: "#fff",
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#a5b4fc",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#6366f1",
                      borderWidth: 2,
                    },
                  },
                }}
              />
            </Paper>

            {/* Profile card */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2,
                mb: 2.5,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "#fff",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  color: "#334155",
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <BadgeOutlinedIcon sx={{ fontSize: 22, color: "#6366f1" }} />
                Profile
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Full Name"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    error={!!formErrors.name}
                    helperText={formErrors.name}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BadgeOutlinedIcon
                            sx={{ color: "#94a3b8", fontSize: 20 }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        backgroundColor: "#fff",
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#a5b4fc",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6366f1",
                          borderWidth: 2,
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Employee ID"
                    value={newEmployeeId}
                    onChange={(e) => setNewEmployeeId(e.target.value)}
                    placeholder="e.g. EMP-001"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        backgroundColor: "#fff",
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#a5b4fc",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6366f1",
                          borderWidth: 2,
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Phone Number"
                    type="tel"
                    value={newPhoneNumber}
                    onChange={(e) => setNewPhoneNumber(e.target.value)}
                    error={!!formErrors.phoneNumber}
                    helperText={formErrors.phoneNumber}
                    placeholder="10 digits"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneOutlinedIcon
                            sx={{ color: "#94a3b8", fontSize: 20 }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        backgroundColor: "#fff",
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#a5b4fc",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6366f1",
                          borderWidth: 2,
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Date of Birth"
                    type="date"
                    value={newDob}
                    onChange={(e) => setNewDob(e.target.value)}
                    error={!!formErrors.dob}
                    helperText={formErrors.dob}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarTodayOutlinedIcon
                            sx={{ color: "#94a3b8", fontSize: 20 }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        backgroundColor: "#fff",
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#a5b4fc",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6366f1",
                          borderWidth: 2,
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Date of Join"
                    type="date"
                    value={newDateOfJoin}
                    onChange={(e) => setNewDateOfJoin(e.target.value)}
                    error={!!formErrors.dateOfJoin}
                    helperText={formErrors.dateOfJoin}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarTodayOutlinedIcon
                            sx={{ color: "#94a3b8", fontSize: 20 }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        backgroundColor: "#fff",
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#a5b4fc",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6366f1",
                          borderWidth: 2,
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      mb: 2,
                      p: 1.75,
                      borderRadius: 2,
                      bgcolor: "#f8fafc",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        color: "#64748b",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        mb: 0.5,
                      }}
                    >
                      Current qualification
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: selectedAdmin?.qualification?.trim()
                          ? "#0f172a"
                          : "#94a3b8",
                        wordBreak: "break-word",
                      }}
                    >
                      {selectedAdmin?.qualification?.trim() || "— Not set —"}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: "2px solid",
                      borderColor: newQualification.trim() ? "#a5b4fc" : "#e2e8f0",
                      bgcolor: newQualification.trim() ? "#f5f3ff" : "#fafafa",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1.5,
                        fontWeight: 700,
                        color: "#4f46e5",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                      }}
                    >
                      <EditOutlinedIcon sx={{ fontSize: 18 }} />
                      Update to new qualification
                    </Typography>

                    <TextField
                      fullWidth
                      size="small"
                      select
                      label="Select new qualification"
                      value={isManualQualification ? "" : qualificationOption}
                      onChange={handleQualificationOptionChange}
                      error={!!formErrors.qualification}
                      helperText={
                        formErrors.qualification ||
                        "Choose from the list or enter manually below"
                      }
                      SelectProps={{ native: true }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          backgroundColor: "#fff",
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#a5b4fc",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#6366f1",
                            borderWidth: 2,
                          },
                        },
                        "& .MuiNativeSelect-select": {
                          py: 1.25,
                        },
                      }}
                    >
                      <option value="">Select qualification</option>
                      {QUALIFICATION_GROUPS.map((group) => (
                        <optgroup key={group.label} label={group.label}>
                          {group.options.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </TextField>

                    <Button
                      type="button"
                      variant="outlined"
                      onClick={handleManualQualificationSelect}
                      sx={{
                        mt: 1.5,
                        width: "100%",
                        justifyContent: "flex-start",
                        textAlign: "left",
                        textTransform: "none",
                        borderStyle: isManualQualification ? "solid" : "dashed",
                        borderWidth: 2,
                        borderColor: "#f59e0b",
                        bgcolor: isManualQualification ? "#fef3c7" : "#fffbeb",
                        color: "#92400e",
                        fontWeight: 600,
                        borderRadius: 2,
                        py: 1.25,
                        px: 1.75,
                        boxShadow: isManualQualification
                          ? "0 4px 14px rgba(245, 158, 11, 0.22)"
                          : "0 2px 8px rgba(245, 158, 11, 0.12)",
                        "&:hover": {
                          bgcolor: "#fef3c7",
                          borderColor: "#d97706",
                        },
                      }}
                    >
                      ✏️{" "}
                      <Box component="span" sx={{ ml: 0.75 }}>
                        <strong>Other — enter manually</strong>
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ display: "block", color: "#b45309", fontWeight: 400 }}
                        >
                          Use if the qualification is not in the list above
                        </Typography>
                      </Box>
                    </Button>

                    {isManualQualification && (
                      <TextField
                        fullWidth
                        size="small"
                        label="Enter new qualification"
                        value={qualificationManual}
                        onChange={(e) =>
                          setQualificationManual(e.target.value.slice(0, 200))
                        }
                        placeholder="Type your qualification"
                        error={!!formErrors.qualification}
                        sx={{
                          mt: 1.5,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            backgroundColor: "#fffbeb",
                            "& fieldset": { borderColor: "#f59e0b" },
                            "&:hover fieldset": { borderColor: "#d97706" },
                            "&.Mui-focused fieldset": {
                              borderColor: "#f59e0b",
                              borderWidth: 2,
                            },
                          },
                        }}
                        autoFocus
                      />
                    )}

                    <Box
                      sx={{
                        mt: 2,
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: "#fff",
                        border: "1px solid",
                        borderColor: newQualification.trim() ? "#6366f1" : "#e2e8f0",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          color: "#6366f1",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                          mb: 0.5,
                        }}
                      >
                        New qualification (will be saved)
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: newQualification.trim() ? "#0f172a" : "#94a3b8",
                          wordBreak: "break-word",
                        }}
                      >
                        {newQualification.trim() ||
                          "— Select or enter a new qualification above —"}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
              {selectedAdmin && isAdminProfileIncomplete(selectedAdmin) && (
                <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
                  Profile incomplete — add date of join and qualification to backfill this admin.
                </Alert>
              )}
            </Paper>

            {/* Role & Status card */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "#fff",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  color: "#334155",
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <WorkOutlineOutlinedIcon sx={{ fontSize: 22, color: "#6366f1" }} />
                Role &amp; Status
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    select
                    label="Role"
                    value={newRole}
                    onChange={(e) => setNewRole(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        backgroundColor: "#fff",
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#a5b4fc",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6366f1",
                          borderWidth: 2,
                        },
                      },
                    }}
                  >
                    {ADMIN_ROLES.map((roleValue) => (
                      <MenuItem key={roleValue} value={roleValue}>
                        {getRoleLabel(roleValue)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    select
                    label="Status"
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        backgroundColor: "#fff",
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#a5b4fc",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6366f1",
                          borderWidth: 2,
                        },
                      },
                    }}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    select
                    label="Manual Mapping"
                    value={newManualMapping}
                    onChange={(e) => setNewManualMapping(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        backgroundColor: "#fff",
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#a5b4fc",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6366f1",
                          borderWidth: 2,
                        },
                      },
                    }}
                  >
                    <MenuItem value="enabled">Enabled</MenuItem>
                    <MenuItem value="disabled">Disabled</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    select
                    label="Object Disinfection"
                    value={newObjectDisinfection}
                    onChange={(e) => setNewObjectDisinfection(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        backgroundColor: "#fff",
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#a5b4fc",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6366f1",
                          borderWidth: 2,
                        },
                      },
                    }}
                  >
                    <MenuItem value="enabled">Enabled</MenuItem>
                    <MenuItem value="disabled">Disabled</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1.5,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "#fff",
          }}
        >
          <Button
            onClick={closeUpdateDialog}
            variant="outlined"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: "#64748b",
              borderColor: "#e2e8f0",
              "&:hover": {
                borderColor: "#94a3b8",
                bgcolor: "#f8fafc",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={isSubmitting}
            onClick={handleUpdateDetails}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
              boxShadow: "0 4px 14px rgba(99, 102, 241, 0.4)",
              "&:hover": {
                background:
                  "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
                boxShadow: "0 6px 20px rgba(99, 102, 241, 0.45)",
              },
              "&:disabled": {
                background: "#cbd5e1",
                color: "#94a3b8",
              },
            }}
          >
            {isSubmitting ? (
              <CircularProgress size={22} sx={{ color: "white" }} />
            ) : (
              "Update Details"
            )}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          style={{
            borderRadius: "12px",
            fontWeight: 500,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {admins.length === 0 && !loading && (
        <Container maxWidth="sm" sx={{ py: 8 }}>
          <Paper
            elevation={0}
            sx={{
              p: 5,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              textAlign: "center",
              bgcolor: "#fff",
            }}
          >
            <Box
              sx={{
                width: 96,
                height: 96,
                borderRadius: 3,
                bgcolor: "rgba(99, 102, 241, 0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <Person sx={{ fontSize: 48, color: "#6366f1" }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: "#0f172a", mb: 1 }}>
              No admin users yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, mx: "auto", mb: 3 }}>
              There are no administrator accounts in the system. Create new admin users from the registration panel.
            </Typography>
            <Button
              variant="contained"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                borderRadius: 2,
                background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                boxShadow: "0 4px 14px rgba(99, 102, 241, 0.35)",
                "&:hover": {
                  background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
                  boxShadow: "0 6px 20px rgba(99, 102, 241, 0.4)",
                },
              }}
            >
              Create New Admin
            </Button>
          </Paper>
        </Container>
      )}
    </Box>
  );
};

export default AdminDetails;
